import React from 'react';
import { usePresence } from '../../hooks/usePresence';
import { useAIActivity } from '../../hooks/useAIActivity';
import UserPresence from './UserPresence';

/**
 * PresenceList component - Shows list of online users
 * Displays total count and individual user badges
 * 
 * MEMOIZED to prevent unnecessary re-renders from parent components
 * NOW RECEIVES onlineUsers as prop from parent
 */
const PresenceList = React.memo(function PresenceList({ onlineUsers = [] }) {
  console.log('🎨 PresenceList RENDERING with', onlineUsers.length, 'users');
  
  const aiActivity = useAIActivity();

  // Debug logging
  React.useEffect(() => {
    console.log('🔍 DEBUG - PresenceList render:', {
      onlineUsersCount: onlineUsers.length,
      users: onlineUsers.map(u => ({
        id: u.userId.substring(0, 8) + '...',
        name: u.displayName,
        isOnline: u.isOnline
      }))
    });
  }, [onlineUsers]);

  if (onlineUsers.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 min-w-[200px]">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          <span>No users online</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 min-w-[200px] max-w-[300px]">
      {/* Header with count */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span className="text-sm font-medium text-gray-700">
          {onlineUsers.length} user{onlineUsers.length !== 1 ? 's' : ''} online
        </span>
      </div>

      {/* Users list */}
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {onlineUsers.map((user) => {
          const isUsingAI = aiActivity.some(a => a.userId === user.userId);
          return (
            <UserPresence
              key={user.userId}
              user={user}
              showTooltip={true}
              isUsingAI={isUsingAI}
            />
          );
        })}
      </div>

      {/* Footer with collaboration hint */}
      {onlineUsers.length > 1 && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            🎨 Collaborating in real-time
          </p>
        </div>
      )}
    </div>
  );
});

/**
 * Compact version for toolbar/header
 * MEMOIZED to prevent unnecessary re-renders
 * NOW RECEIVES onlineUsers as prop from parent
 */
export const CompactPresenceList = React.memo(function CompactPresenceList({ onlineUsers = [] }) {
  console.log('🎨 CompactPresenceList RENDERING with', onlineUsers.length, 'users');

  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
      {/* Online indicator */}
      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
      
      {/* User count */}
      <span className="text-sm font-medium text-gray-700">
        {onlineUsers.length}
      </span>
      
      {/* User avatars (max 3 visible) */}
      <div className="flex -space-x-1">
        {onlineUsers.slice(0, 3).map((user, index) => (
          <div
            key={user.userId}
            className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-white"
            style={{ 
              backgroundColor: user.cursorColor,
              zIndex: onlineUsers.length - index
            }}
            title={user.displayName}
          >
            {user.displayName.charAt(0).toUpperCase()}
          </div>
        ))}
        
        {/* Show +N if more than 3 users */}
        {onlineUsers.length > 3 && (
          <div className="w-6 h-6 rounded-full bg-gray-400 border-2 border-white flex items-center justify-center text-xs font-bold text-white">
            +{onlineUsers.length - 3}
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Minimal dot indicator for status bar
 * MEMOIZED to prevent unnecessary re-renders
 * NOW RECEIVES onlineUsers as prop from parent
 */
export const PresenceDot = React.memo(function PresenceDot({ onlineUsers = [] }) {
  console.log('🎨 PresenceDot RENDERING with', onlineUsers.length, 'users');
  
  if (onlineUsers.length === 0) {
    return (
      <div className="flex items-center gap-1 text-xs text-gray-500">
        <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
        <span>Offline</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-xs text-gray-600">
      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
      <span>{onlineUsers.length} online</span>
    </div>
  );
});

export default PresenceList;
